import React from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { Wrench, ArrowRight, Flame } from "lucide-react";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import PreFooterCTA from "../components/PreFooterCTA";
import { SERVICES, SERVICE_CATEGORIES, BUSINESS } from "../data/business";

// SERVICES/SERVICE_CATEGORIES are module constants — group once at module load.
const SERVICES_BY_CATEGORY = SERVICE_CATEGORIES.reduce((acc, cat) => {
  acc[cat] = SERVICES.filter((s) => s.category === cat);
  return acc;
}, {});

const BREADCRUMBS = [{ label: "Services" }];

// Hero featured service card — keeps drain cleaning front and center.
function FeaturedServiceBanner() {
  const wh = SERVICES.find((s) => s.slug === "drain-cleaning");
  if (!wh) return null;
  return (
    <Link
      to={`/services/${wh.slug}`}
      className="mt-8 block max-w-3xl rounded-2xl border border-white/15 bg-white/8 backdrop-blur p-5 hover:bg-white/12 transition group"
      data-testid="services-featured-banner"
    >
      <div className="flex items-center gap-4">
        <span className="w-12 h-12 rounded-xl bg-[#E31D2B] text-white inline-flex items-center justify-center flex-shrink-0">
          <Flame className="w-6 h-6" />
        </span>
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#E31D2B]">Featured service</div>
          <div className="font-display font-extrabold text-lg sm:text-xl text-white">Drain Cleaning · Baltimore &amp; DC</div>
          <div className="text-sm text-white/75 mt-0.5">Slow or clogged drains cleared fast — kitchen, bath, and main lines.</div>
        </div>
        <ArrowRight className="w-5 h-5 text-white/75 group-hover:translate-x-1 transition" />
      </div>
    </Link>
  );
}

export default function Services() {
  return (
    <Layout>
      <section className="bg-[#1A1D21] text-white">
        <div className="abc-container py-12 md:py-16">
          <Breadcrumbs items={BREADCRUMBS} />
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold">Drain &amp; sewer services across <span className="text-[#E31D2B]">Baltimore &amp; DC</span></h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">Drain and sewer specialists — that's all we do. Drain cleaning is our featured service; we also handle sewer camera inspection, hydro-jetting, pipe descaling, sewer &amp; water line replacement, sump pumps, and basement waterproofing across the Baltimore and DC metros.</p>
          <p className="mt-2 text-white/70 text-sm">24/7 emergency service — open day &amp; night. Call <a href={BUSINESS.phoneHref} className="underline hover:text-white">{BUSINESS.phone}</a> to schedule.</p>
          <FeaturedServiceBanner />
        </div>
      </section>

      {SERVICE_CATEGORIES.map((cat) => (
        <section key={cat} className="py-12 md:py-14 odd:abc-bg-pale" data-testid={`services-category-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="abc-container">
            <div className="text-xs font-bold uppercase tracking-widest text-[#54616E] mb-3">Category</div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">{cat}</h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {SERVICES_BY_CATEGORY[cat].map((s) => {
                const Icon = LucideIcons[s.icon] || Wrench;
                return (
                  <Link to={`/services/${s.slug}`} key={s.slug} className="abc-card p-6 group" data-testid={`services-card-${s.slug}`}>
                    <div className="w-11 h-11 rounded-xl bg-[#EDEFF2] text-[#54616E] inline-flex items-center justify-center mb-4 group-hover:bg-[#54616E] group-hover:text-white transition">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#1A1D21]">{s.name}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#54616E]">Learn more <ArrowRight className="w-4 h-4" /></span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      <PreFooterCTA />
    </Layout>
  );
}
